import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { CalendarEditorModal } from './components/calendar-editor-modal';
import { BookingWindowSettings } from './components/booking-window-settings';
import { ConnectionsTable } from './components/connections-table';
import { RequestsCalendar } from './components/requests-calendar';
import { GoogleCalendarNotConfiguredBanner } from './components/google-calendar-not-configured-banner';
import { useSchedulingConnections } from './hooks/use-scheduling-connections';
import { useSchedulingRequests } from './hooks/use-scheduling-requests';
import type { SchedulingConnection } from './types/scheduling.types';

export function SchedulingTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthMessage, setOauthMessage] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<SchedulingConnection | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const {
    connections,
    isLoading: isConnectionsLoading,
    isError: isConnectionsError,
    isGoogleCalendarNotConfigured,
    connectCalendar,
    isStartingOAuth,
    saveCalendarSelection,
    isSavingCalendars,
    revokeConnectionAccess,
    actionsDisabled,
  } = useSchedulingConnections();

  const {
    visibleMonth,
    selectedDay,
    setSelectedDay,
    requestsByDay,
    isLoading: isRequestsLoading,
    isError: isRequestsError,
    goToPreviousMonth,
    goToNextMonth,
  } = useSchedulingRequests();

  useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    if (!oauthStatus) {
      return;
    }

    if (oauthStatus === 'success') {
      setOauthMessage('Google Calendar connected successfully.');
    } else if (oauthStatus === 'error') {
      setOauthMessage('Google Calendar connection failed. Please try again.');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('oauth');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnectCalendar = async() => {
    setConnectError(null);
    const result = await connectCalendar();
    if (!result.ok) {
      setConnectError(result.message ?? 'Could not start Google Calendar connection.');
    }
  };

  const handleSelectConnection = (connection: SchedulingConnection) => {
    setSelectedConnection(connection);
    setIsEditorOpen(true);
  };

  return (
    <div className={cn(portalDs.page, 'h-full overflow-y-auto px-6 py-8 sm:px-10')}>
      {isGoogleCalendarNotConfigured ? (
        <GoogleCalendarNotConfiguredBanner />
      ) : null}

      {oauthMessage ? (
        <p className={cn(portalDs.caption, 'mb-4 text-[#2563EB]')}>
          {oauthMessage}
        </p>
      ) : null}

      <section className={cn(isGoogleCalendarNotConfigured ? 'mt-6' : '')}>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[18px] font-semibold text-[#0D0F1A]">
              Booking pool
            </h2>
            <p className={cn(portalDs.caption, 'mt-1')}>
              Connected calendars available for client scheduling calls.
            </p>
          </div>
          <button
            type="button"
            disabled={actionsDisabled || isStartingOAuth}
            onClick={() => void handleConnectCalendar()}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0D0F1A] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2A2D3E] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isStartingOAuth ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                Connecting…
              </>
            ) : 'Add your calendar'}
          </button>
        </div>

        <BookingWindowSettings disabled={actionsDisabled} />

        {connectError ? (
          <p className={cn(portalDs.caption, 'mb-4 text-[#DC2626]')}>
            {connectError}
          </p>
        ) : null}

        {isConnectionsLoading ? (
          <div className="mb-8 flex items-center gap-2 text-[#5E6278]">
            <Loader2
              className="size-4 animate-spin"
              aria-hidden
            />
            <span className={portalDs.caption}>
              Loading booking pool…
            </span>
          </div>
        ) : isConnectionsError && !isGoogleCalendarNotConfigured ? (
          <p className={cn(portalDs.caption, 'mb-8 text-[#DC2626]')}>
            Could not load the booking pool.
          </p>
        ) : (
          <div className="mb-10">
            <ConnectionsTable
              connections={connections}
              onSelectConnection={handleSelectConnection}
              disabled={actionsDisabled}
            />
          </div>
        )}
      </section>

      <section>
        <div className="mb-4">
          <h2 className="text-[18px] font-semibold text-[#0D0F1A]">
            Meeting requests
          </h2>
          <p className={cn(portalDs.caption, 'mt-1')}>
            Upcoming and recent client scheduling requests.
          </p>
        </div>

        {isRequestsLoading ? (
          <div className="flex items-center gap-2 text-[#5E6278]">
            <Loader2
              className="size-4 animate-spin"
              aria-hidden
            />
            <span className={portalDs.caption}>
              Loading meeting requests…
            </span>
          </div>
        ) : isRequestsError ? (
          <p className={cn(portalDs.caption, 'text-[#DC2626]')}>
            Could not load meeting requests.
          </p>
        ) : (
          <RequestsCalendar
            visibleMonth={visibleMonth}
            requestsByDay={requestsByDay}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            onPreviousMonth={goToPreviousMonth}
            onNextMonth={goToNextMonth}
          />
        )}
      </section>

      <CalendarEditorModal
        connection={selectedConnection}
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
        onSave={saveCalendarSelection}
        onRevoke={revokeConnectionAccess}
        isSaving={isSavingCalendars}
      />
    </div>
  );
}
